declare global {
  interface Window {
    __ENV__?: Record<string, string>;
  }
}

export interface EnvConfig {
  VITE_API_URL: string;
  VITE_AUTH_DOMAIN: string;
  VITE_RELEASE_SHA?: string;
  VITE_MIXPANEL_PROXY_BASE?: string;
  VITE_SENTRY_DSN?: string;
  VITE_SENTRY_ENABLED?: string;
  VITE_SENTRY_ENVIRONMENT?: string;
  /** Same-origin Sentry tunnel path (e.g. `/_t/e/1`); empty/unset = direct ingest. */
  VITE_SENTRY_TUNNEL?: string;
  /** Address users contact for help (sign-in, two-step verification, errors). */
  VITE_SUPPORT_EMAIL?: string;
  /** General contact address (footers, legal texts, access requests). */
  VITE_CONTACT_EMAIL?: string;
  /** Contact address shown in Canvas UI. */
  VITE_DIALOGUE_EMAIL?: string;
  [key: string]: string | undefined;
}

export const env = new Proxy({} as EnvConfig, {
  get(_, prop: string) {
    if (typeof window !== "undefined" && window.__ENV__ && window.__ENV__[prop] !== undefined) {
      return window.__ENV__[prop];
    }
    const meta = typeof import.meta !== "undefined" && import.meta?.env ? import.meta.env : {};
    return (meta as Record<string, unknown>)[prop];
  },
});

/**
 * Read a required address from the app's runtime env (`config/runtime/.env.*`).
 * There is deliberately no fallback: a missing value fails loudly so the env
 * files stay the single source of truth.
 */
function requireAddress(name: "VITE_SUPPORT_EMAIL" | "VITE_CONTACT_EMAIL" | "VITE_DIALOGUE_EMAIL"): string {
  const value = env[name]?.trim();
  if (!value) {
    throw new Error(`${name} is not set; add it to config/runtime/.env.*`);
  }
  return value;
}

export const supportEmail = (): string => requireAddress("VITE_SUPPORT_EMAIL");
export const contactEmail = (): string => requireAddress("VITE_CONTACT_EMAIL");
export const dialogueEmail = (): string => requireAddress("VITE_DIALOGUE_EMAIL");
